import { lazy, type LazyExoticComponent, type ComponentType } from "react";

export type SectionPageConfig = {
  slug: string;
  label: string;
  group: "Platform" | "Solutions" | "Trust" | "Investors" | "Company";
  title: string;
  /** Visible page H1; falls back to `label`. */
  heading?: string;
  description: string;
  Component: LazyExoticComponent<ComponentType<unknown>>;
};

export const sectionPages: SectionPageConfig[] = [
  /* /how-it-works now has a dedicated page — see src/pages/HowItWorks.tsx */
  {
    slug: "technology",
    heading: "Product and technology overview",
    label: "Technology",
    group: "Platform",
    title: "Product & Technology Overview | datasyteAI™",
    description:
      "Product and technology overview for AiMediQ™ powered by datasyteAI™, including architecture and patent information.",
    Component: lazy(() => import("@/components/TechnologySection").then((m) => ({ default: m.TechnologySection }))),
  },
  {
    slug: "train",
    heading: "Trained to your experts and protocols",
    label: "Train",
    group: "Platform",
    title: "Trained to Your Experts & Protocols | datasyteAI™",
    description:
      "datasyteAI™ is trained to your institution's experts, protocols, and standards of care.",
    Component: lazy(() => import("@/components/TrainSection").then((m) => ({ default: m.TrainSection }))),
  },
  {
    slug: "benefits",
    heading: "What improves during a Code Blue",
    label: "Benefits",
    group: "Platform",
    title: "What Improves with datasyteAI™ | AiMediQ™",
    description:
      "What improves with datasyteAI™: speed, consistency, documentation, and confidence during a Code Blue.",
    Component: lazy(() => import("@/components/BenefitsGallery").then((m) => ({ default: m.BenefitsGallery }))),
  },
  {
    slug: "video",
    heading: "What is a Code Blue?",
    label: "Video",
    group: "Platform",
    title: "What Is a Code Blue? Video Explainer | datasyteAI™",
    description: "A short video explainer on what a Code Blue is and why response quality varies.",
    Component: lazy(() => import("@/components/VideoSection").then((m) => ({ default: m.VideoSection }))),
  },
  {
    slug: "flip-book",
    heading: "Interactive flip book",
    label: "Interactive Flip Book",
    group: "Company",
    title: "Interactive Flip Book | AiMediQ™ powered by datasyteAI™",
    description:
      "Explore our interactive flip book to learn more about AiMediQ™ powered by datasyteAI™.",
    Component: lazy(() => import("@/components/FlipBookSection").then((m) => ({ default: m.FlipBookSection }))),
  },
  {
    slug: "problem",
    heading: "The problem in in-hospital cardiac arrest",
    label: "Problem",
    group: "Trust",
    title: "The Problem — Why Code Blue Outcomes Vary | datasyteAI™",
    description: "Why Code Blue outcomes vary across teams, shifts, and facilities.",
    Component: lazy(() => import("@/components/ProblemSection").then((m) => ({ default: m.ProblemSection }))),
  },
  {
    slug: "guardrails",
    heading: "Clinical guardrails and human oversight",
    label: "Guardrails",
    group: "Trust",
    title: "Security, Privacy & Safety Guardrails | datasyteAI™",
    description:
      "Security, privacy, and safety limits behind AiMediQ™, including quantum-resistant security work in progress.",
    Component: lazy(() => import("@/components/SecuritySection").then((m) => ({ default: m.SecuritySection }))),
  },
  {
    slug: "regulatory-status",
    heading: "Regulatory status",
    label: "Regulatory Status",
    group: "Trust",
    title: "Regulatory Status | AiMediQ™ powered by datasyteAI™",
    description:
      "AiMediQ™ powered by datasyteAI™ is in the pre-market development and validation phase. Read the regulatory status.",
    Component: lazy(() => import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection }))),
  },
  {
    slug: "market",
    heading: "Market context",
    label: "Market",
    group: "Investors",
    title: "Market Opportunity | datasyteAI™",
    description: "Market size and opportunity for AI-assisted resuscitation decision support.",
    Component: lazy(() => import("@/components/MarketSection").then((m) => ({ default: m.MarketSection }))),
  },
  {
    slug: "business-model",
    heading: "Commercial model",
    label: "Model",
    group: "Investors",
    title: "Business Model & Financial Strategy | datasyteAI™",
    description: "Business model and financial strategy for AiMediQ™ powered by datasyteAI™.",
    Component: lazy(() => import("@/components/BusinessSection").then((m) => ({ default: m.BusinessSection }))),
  },
  {
    slug: "competition",
    heading: "Category landscape",
    label: "Competition",
    group: "Investors",
    title: "Competitive Landscape | datasyteAI™",
    description: "How AiMediQ™ powered by datasyteAI™ compares to existing approaches.",
    Component: lazy(() => import("@/components/CompetitionSection").then((m) => ({ default: m.CompetitionSection }))),
  },
  {
    slug: "contact",
    heading: "Contact PathFree",
    label: "Contact",
    group: "Company",
    title: "Contact PathFree Technologies | datasyteAI™",
    description: "Get in touch with PathFree Technologies Corporation about AiMediQ™ and datasyteAI™.",
    Component: lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection }))),
  },
  {
    slug: "for-executives",
    heading: "For health system executives",
    label: "For Executives",
    group: "Solutions",
    title: "For Health-System Executives | AiMediQ™ powered by datasyteAI™",
    description:
      "How AiMediQ™ powered by datasyteAI™ fits health-system priorities for standardization, quality, and risk.",
    Component: lazy(() => import("@/components/AudiencePages").then((m) => ({ default: m.ForExecutives }))),
  },
  {
    slug: "for-clinical-teams",
    heading: "For clinical and resuscitation teams",
    label: "For Clinical Teams",
    group: "Solutions",
    title: "For Clinical & Code Teams | AiMediQ™ powered by datasyteAI™",
    description:
      "How datasyteAI™ supports Code Blue and rapid-response teams with next-step prompts, timing cues, and documentation.",
    Component: lazy(() => import("@/components/AudiencePages").then((m) => ({ default: m.ForClinicalTeams }))),
  },
  {
    slug: "for-healthcare-it",
    heading: "For healthcare IT, security, and integration",
    label: "For Healthcare IT",
    group: "Solutions",
    title: "For Healthcare IT & Security Leaders | AiMediQ™ powered by datasyteAI™",
    description:
      "Architecture, security posture, data handling, and integration approach for AiMediQ™ powered by datasyteAI™.",
    Component: lazy(() => import("@/components/AudiencePages").then((m) => ({ default: m.ForHealthcareIT }))),
  },
];

export const sectionPageBySlug = Object.fromEntries(
  sectionPages.map((page) => [page.slug, page])
) as Record<string, SectionPageConfig>;
